import { useQueryClient } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { canDeleteMenuSet, deleteMenuSet, isMenuSetNotDeletableError } from 'src/api/deleteMenuSet';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useDeleteEntityPrompt } from 'src/hooks/useDeleteEntityPrompt';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

interface UseDeleteMenuSetPromptArgs {
  menuSetId: string;
  menuSetName?: string;
  genericErrorMessage: string;
  onDeleted?: () => void;
}

export const useDeleteMenuSetPrompt = ({
  menuSetId,
  menuSetName,
  genericErrorMessage,
  onDeleted,
}: UseDeleteMenuSetPromptArgs) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { trackEvent, captureException } = useUserTracking();

  return useDeleteEntityPrompt({
    entityId: menuSetId,
    canDelete: canDeleteMenuSet,
    deleteEntity: deleteMenuSet,
    isNotDeletableError: isMenuSetNotDeletableError,
    genericErrorMessage,
    onDeleted: async () => {
      trackEvent('menu_set_deleted', { menuSetId, menuSetName });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully deleted “${menuSetName}”.`,
      });
      onDeleted?.();
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menuSets] });
    },
    onCheckError: (err) => captureException(err, { tags: { action: 'delete_menu_set_check', menuSetId } }),
    onDeleteError: (err) => captureException(err, { tags: { action: 'delete_menu_set', menuSetId } }),
  });
};
